import { useEffect, useRef, useState } from "react";
import { createPortal } from "react-dom";
import { BookOpen, FileText, Layers, type LucideIcon } from "lucide-react";

const KIND_ICONS: Record<string, LucideIcon> = {
  U: Layers,
  L: BookOpen,
  I: FileText,
};

/**
 * Outline kind mark: icon only, label in a custom tooltip.
 *
 * Hover or focus opens the tip. Double-click pins it until another
 * double-click, click-away, or Escape. A single click can expand a
 * unit/lesson row (`onIdleClick`) without fighting the pin.
 */
export default function KindBadge({
  mark,
  label,
  onIdleClick,
}: {
  mark: string;
  label: string;
  onIdleClick?: () => void;
}) {
  const rootRef = useRef<HTMLButtonElement>(null);
  const idleClickTimer = useRef<number | null>(null);
  const [hover, setHover] = useState(false);
  const [pinned, setPinned] = useState(false);
  const [coords, setCoords] = useState({ top: 0, left: 0, flip: false });
  const open = hover || pinned;

  const cancelIdleClick = () => {
    if (idleClickTimer.current) {
      window.clearTimeout(idleClickTimer.current);
      idleClickTimer.current = null;
    }
  };

  useEffect(() => {
    return () => cancelIdleClick();
  }, []);

  const place = () => {
    const el = rootRef.current;
    if (!el) return;
    const box = el.getBoundingClientRect();
    setCoords({
      top: box.top < 36 ? box.bottom : box.top,
      left: box.left + box.width / 2,
      flip: box.top < 36,
    });
  };

  const show = () => {
    place();
    setHover(true);
  };

  const hide = () => setHover(false);

  useEffect(() => {
    if (!open) return undefined;
    place();
    const onPointerDown = (event: PointerEvent) => {
      if (rootRef.current && !rootRef.current.contains(event.target as Node)) {
        setPinned(false);
        setHover(false);
      }
    };
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") {
        setPinned(false);
        setHover(false);
      }
    };
    const onMove = () => {
      if (pinned) place();
      else setHover(false);
    };
    document.addEventListener("pointerdown", onPointerDown);
    document.addEventListener("keydown", onKey);
    window.addEventListener("scroll", onMove, true);
    window.addEventListener("resize", onMove);
    return () => {
      document.removeEventListener("pointerdown", onPointerDown);
      document.removeEventListener("keydown", onKey);
      window.removeEventListener("scroll", onMove, true);
      window.removeEventListener("resize", onMove);
    };
  }, [open, pinned]);

  const Icon = KIND_ICONS[mark] || FileText;

  return (
    <>
      <button
        ref={rootRef}
        type="button"
        className={`al-cb-kind-badge al-cb-kind-badge--${mark}${pinned ? " al-cb-kind-badge--on" : ""}`}
        aria-label={label}
        aria-expanded={open}
        onMouseEnter={show}
        onMouseLeave={hide}
        onFocus={show}
        onBlur={() => {
          setHover(false);
          setPinned(false);
        }}
        onClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          cancelIdleClick();
          if (!onIdleClick) return;
          idleClickTimer.current = window.setTimeout(() => {
            idleClickTimer.current = null;
            onIdleClick();
          }, 280);
        }}
        onDoubleClick={(event) => {
          event.preventDefault();
          event.stopPropagation();
          cancelIdleClick();
          if (pinned) {
            setPinned(false);
            return;
          }
          place();
          setPinned(true);
        }}
      >
        <Icon size={13} strokeWidth={2.25} aria-hidden="true" />
      </button>
      {open
        ? createPortal(
            <span
              className={`al-cb-kind-tip${coords.flip ? " al-cb-kind-tip--below" : ""}`}
              role="tooltip"
              style={{ top: coords.top, left: coords.left }}
            >
              {label}
            </span>,
            document.body,
          )
        : null}
    </>
  );
}
